<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import BarChart from './BarChart.vue'
import DateRangePicker from './DateRangePicker.vue'
import { Settings, Calendar, X, Filter } from 'lucide-vue-next'

import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import { Pie } from 'vue-chartjs'
import { Download } from 'lucide-vue-next'
import jsPDF from 'jspdf'
import { Line } from 'vue-chartjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'

import 'jspdf-autotable'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
)

const store = useStore()
const route = useRoute()

const loading = ref(true)
const npsData = computed(() => store.getters['analytics/npsData'])

const start = new Date()
start.setDate(1)
start.setHours(0, 0, 0, 0)
const end = new Date()
const showFiltersMenu = ref(false)
const showMobileMenu = ref(false)
const isMobile = ref(window.innerWidth < 768)

const dateRange = ref({
  start: start,
  end: end
})

const handleResize = () => {
  isMobile.value = window.innerWidth < 768
  if (isMobile.value && showFiltersMenu.value) {
    // Close filters menu when transitioning to desktop to avoid UI issues
    showFiltersMenu.value = false
  }
}

const filterType = ref('app,survey_sparrow')
const filterOptions = [
  { value: 'app,survey_sparrow', label: 'All Channels' },
  { value: 'app', label: 'App' },
  { value: 'survey_sparrow', label: 'Email' }
]

const handleFilterChange = () => {
  getNPSData(
    formatLocalDate(dateRange.value.start),
    formatLocalDate(dateRange.value.end)
  )
}

const handleDateRangeChange = () => {
  const startDate = new Date(dateRange.value.start)
  const endDate = new Date(dateRange.value.end)

  // Format dates to YYYY-MM-DD format without timezone conversion issues
  const from = formatLocalDate(startDate)
  const to = formatLocalDate(endDate)

  displayStartDate = from
  displayEndDate = to
  getNPSData(from, to)
}

const formatLocalDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// For direct date inputs in mobile view
const startDate = computed({
  get: () => formatLocalDate(dateRange.value.start),
  set: (value) => {
    if (!value) return
    const start = new Date(value)
    start.setHours(0, 0, 0, 0)
    dateRange.value.start = start
  }
})

const endDate = computed({
  get: () => formatLocalDate(dateRange.value.end),
  set: (value) => {
    if (!value) return
    const end = new Date(value)
    end.setHours(23, 59, 59, 999)
    dateRange.value.end = end
  }
})

let displayStartDate =
  route.query.date_from ?? formatLocalDate(dateRange.value.start)

let displayEndDate = route.query.date_to ?? formatLocalDate(dateRange.value.end)

const getNPSData = async (from, to) => {
  try {
    loading.value = true
    const params = {
      start_date: from,
      end_date: to,
      source: filterType.value
    }
    await store.dispatch('analytics/getNPSData', params)
  } catch (error) {
    console.error('Error:', error)
  } finally {
    loading.value = false
  }
}

const setPredefinedRange = (range) => {
  const now = new Date()
  const start = new Date()

  switch (range) {
    case 'today':
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
    case 'yesterday':
      start.setDate(start.getDate() - 1)
      start.setHours(0, 0, 0, 0)
      const yesterdayEnd = new Date(start)
      yesterdayEnd.setHours(23, 59, 59, 999)
      dateRange.value = { start, end: yesterdayEnd }
      break
    case 'week':
      start.setDate(start.getDate() - 7)
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
    case 'month':
      start.setMonth(start.getMonth() - 1)
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
    case 'quarter':
      start.setMonth(start.getMonth() - 3)
      start.setHours(0, 0, 0, 0)
      dateRange.value = { start, end: now }
      break
  }

  // Automatically apply the change
  handleDateRangeChange()
}

const pieChartData = computed(() => {
  if (!npsData.value?.overall) return null
  return {
    labels: ['Detractors', 'Passives', 'Promoters'],
    datasets: [
      {
        data: [
          npsData.value.overall.detractors,
          npsData.value.overall.passives,
          npsData.value.overall.promoters
        ],
        backgroundColor: ['#ef4444', '#f59e0b', '#22c55e']
      }
    ]
  }
})

const languageChartData = computed(() => {
  if (!npsData.value?.by_language) return null
  return {
    labels: Object.keys(npsData.value.by_language).map((code) => {
      try {
        const languageNames = new Intl.DisplayNames(['en'], {
          type: 'language'
        })
        return languageNames.of(code)
      } catch (error) {
        return code
      }
    }),
    datasets: [
      {
        label: 'NPS Score',
        data: Object.values(npsData.value.by_language).map(
          (data) => data.nps_score
        ),
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        },
        backgroundColor: '#3b82f6'
      },
      {
        label: 'Total Responses',
        data: Object.values(npsData.value.by_language).map(
          (data) => data.total_responses
        ),
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        },
        backgroundColor: '#64748b'
      }
    ]
  }
})

const industryChartData = computed(() => {
  if (!npsData.value?.by_industry) return null
  return {
    labels: Object.keys(npsData.value.by_industry),
    datasets: [
      {
        label: 'NPS Score',
        data: Object.values(npsData.value.by_industry).map(
          (data) => data.nps_score
        ),
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        }
      },
      {
        label: 'Total Responses',
        data: Object.values(npsData.value.by_industry).map(
          (data) => data.total_responses
        ),
        backgroundColor: '#64748b',
        datalabels: {
          color: '#ffffff',
          anchor: 'end',
          align: 'top',
          font: {
            weight: 'bold'
          }
        }
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#fff',
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: 12,
      displayColors: true
    }
  },
  scales: {
    y: {
      ticks: { color: '#fff' },
      grid: {
        color: '#374151'
      }
    },
    x: {
      ticks: { color: '#fff' },
      grid: {
        color: '#374151'
      }
    }
  }
}

// monthly

// Helper function to safely check the monthly metrics data
const hasValidMonthlyMetrics = computed(() => {
  return (
    npsData.value &&
    npsData.value.monthly_metrics &&
    Object.keys(npsData.value.monthly_metrics).length > 0
  )
})

// Helper function to format month display with year
const formatMonthDisplay = (yearMonth) => {
  // yearMonth format is now "YYYY-MM"
  const [year, month] = yearMonth.split('-').map((num) => parseInt(num, 10))
  const date = new Date(year, month - 1, 1) // First day of the month
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

const monthlyChartData = computed(() => {
  // Return null if monthly metrics are not available
  if (!hasValidMonthlyMetrics.value) {
    console.log('No valid monthly metrics available')
    return null
  }

  try {
    // Get the sorted year-months
    const sortedYearMonths = Object.keys(npsData.value.monthly_metrics).sort()

    // Map the data
    const monthlyData = sortedYearMonths.map((yearMonth) => {
      const metrics = npsData.value.monthly_metrics[yearMonth]
      return {
        date: formatMonthDisplay(yearMonth),
        nps_score: metrics.nps_score,
        responses: metrics.responses
      }
    })

    return {
      labels: monthlyData.map((m) => m.date),
      datasets: [
        {
          label: 'Monthly NPS Score',
          data: monthlyData.map((m) => m.nps_score),
          borderColor: '#3b82f6', // blue
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y'
        },
        {
          label: 'Monthly Responses',
          data: monthlyData.map((m) => m.responses),
          borderColor: '#f59e0b', // Amber
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y1'
        }
      ]
    }
  } catch (error) {
    console.error('Error generating monthly chart data:', error)
    return null
  }
})

// Cumulative Monthly NPS Chart using direct monthly_metrics with YYYY-MM format
const cumulativeMonthlyChartData = computed(() => {
  // Return null if monthly metrics are not available
  if (!hasValidMonthlyMetrics.value) {
    return null
  }

  try {
    // Get the sorted year-months
    const sortedYearMonths = Object.keys(npsData.value.monthly_metrics).sort()

    // Calculate cumulative metrics
    let cumulativeResponses = 0
    let cumulativePromoters = 0
    let cumulativeDetractors = 0

    const cumulativeData = sortedYearMonths.map((yearMonth) => {
      const metrics = npsData.value.monthly_metrics[yearMonth]

      // Add to cumulative counts
      cumulativeResponses += metrics.responses
      cumulativePromoters += metrics.promoters
      cumulativeDetractors += metrics.detractors

      // Calculate NPS based on all data to this point
      const npsScore =
        cumulativeResponses === 0
          ? 0
          : Math.round(
              ((cumulativePromoters - cumulativeDetractors) /
                cumulativeResponses) *
                100
            )

      return {
        date: formatMonthDisplay(yearMonth),
        nps: npsScore,
        responses: cumulativeResponses
      }
    })

    return {
      labels: cumulativeData.map((d) => d.date),
      datasets: [
        {
          label: 'Cumulative NPS',
          data: cumulativeData.map((d) => d.nps),
          borderColor: '#3b82f6', // blue
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y'
        },
        {
          label: 'Cumulative Responses',
          data: cumulativeData.map((d) => d.responses),
          borderColor: '#f59e0b', // Amber
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          tension: 0.4,
          fill: true,
          yAxisID: 'y1'
        }
      ]
    }
  } catch (error) {
    console.error('Error generating cumulative monthly chart data:', error)
    return null
  }
})

const dailyChartData = computed(() => {
  if (!npsData.value?.daily_metrics) return null

  const sortedDates = Object.keys(npsData.value.daily_metrics).sort()
  const metrics = sortedDates.map((date) => npsData.value.daily_metrics[date])

  return {
    labels: sortedDates,
    datasets: [
      {
        label: 'NPS Score',
        data: metrics.map((m) => m.nps_score),
        borderColor: '#3b82f6', // blue
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y'
      },
      {
        label: 'Total Responses',
        data: metrics.map((m) => m.responses),
        borderColor: '#f59e0b', // Amber
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y1'
      }
    ]
  }
})

const cumulativeChartData = computed(() => {
  if (!npsData.value?.daily_metrics) return null

  const sortedDates = Object.keys(npsData.value.daily_metrics).sort()
  const metrics = sortedDates.map((date) => npsData.value.daily_metrics[date])

  let cumulativeResponses = 0
  let cumulativePromoters = 0
  let cumulativeDetractors = 0

  const cumulativeData = metrics.reduce((acc, metric, index) => {
    // Calculate running totals
    const totalPromoters = metrics
      .slice(0, index + 1)
      .reduce((sum, m) => sum + m.promoters, 0)
    const totalDetractors = metrics
      .slice(0, index + 1)
      .reduce((sum, m) => sum + m.detractors, 0)
    const totalResponses = metrics
      .slice(0, index + 1)
      .reduce((sum, m) => sum + m.responses, 0)

    // Calculate current NPS based on all responses up to this point
    const currentNPS = Math.round(
      ((totalPromoters - totalDetractors) / totalResponses) * 100
    )

    return [
      ...acc,
      {
        responses: totalResponses,
        nps: currentNPS
      }
    ]
  }, [])

  return {
    labels: sortedDates,
    datasets: [
      {
        label: 'Overall NPS Score',
        data: cumulativeData.map((d) => d.nps),
        borderColor: '#3b82f6', // blue
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y'
      },
      {
        label: 'Cumulative Responses',
        data: cumulativeData.map((d) => d.responses),
        borderColor: '#f59e0b', // Amber
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y1'
      }
    ]
  }
})

const cumulativeChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#fff',
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: 12,
      callbacks: {
        label: function (context) {
          let label = context.dataset.label || ''
          if (label) {
            label += ': '
          }
          if (context.dataset.yAxisID === 'y') {
            label += context.parsed.y + ' NPS'
          } else {
            label += context.parsed.y.toFixed(0) + ' Responses'
          }
          return label
        }
      }
    },
    datalabels: {
      display: false
    }
  },
  scales: {
    x: {
      ticks: {
        color: '#fff',
        maxRotation: 45,
        minRotation: 45
      },
      grid: {
        color: '#374151'
      }
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      ticks: { color: '#fff' },
      grid: {
        color: '#374151'
      },
      title: {
        display: true,
        text: 'NPS Score',
        color: '#fff'
      },
      min: -100,
      max: 100
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      ticks: { color: '#fff' },
      grid: {
        drawOnChartArea: false
      },
      title: {
        display: true,
        text: 'Cumulative Responses',
        color: '#fff'
      },
      min: 0
    }
  }
}))

const lineChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#fff',
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: 12,
      callbacks: {
        label: function (context) {
          let label = context.dataset.label || ''
          if (label) {
            label += ': '
          }
          if (context.dataset.yAxisID === 'y') {
            label += context.parsed.y
          } else {
            label += context.parsed.y.toFixed(0)
          }
          return label
        }
      }
    },
    datalabels: {
      display: false
    }
  },
  scales: {
    x: {
      ticks: {
        color: '#fff',
        maxRotation: 45,
        minRotation: 45
      },
      grid: {
        color: '#374151'
      }
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      ticks: { color: '#fff' },
      grid: {
        color: '#374151'
      },
      title: {
        display: true,
        text: 'NPS Score',
        color: '#fff'
      },
      min: -100,
      max: 100
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      ticks: { color: '#fff' },
      grid: {
        drawOnChartArea: false
      },
      title: {
        display: true,
        text: 'Total Responses',
        color: '#fff'
      },
      min: 0
    }
  }
}))

const pieChartRef = ref(null)
const languageChartRef = ref(null)
const industryChartRef = ref(null)

const generatePDF = () => {
  try {
    const doc = new jsPDF()

    // Colors
    const colors = {
      primary: [59, 130, 246],
      lightGray: [245, 247, 250],
      darkGray: [31, 41, 55],
      white: [255, 255, 255]
    }

    // Cover page
    doc.setFillColor(...colors.primary)
    doc.rect(0, 0, 220, 297, 'F')

    doc.setDrawColor(...colors.white)
    doc.setLineWidth(0.5)
    doc.line(20, 75, 190, 75)

    doc.setTextColor(...colors.white)
    doc.setFontSize(42)
    doc.text('NPS Analysis', 20, 50)

    doc.setFontSize(20)
    doc.text('Dashboard Report', 20, 95)

    doc.setFontSize(12)
    doc.text(`Generated ${new Date().toLocaleDateString()}`, 20, 115)
    doc.text(
      `Date Range: ${npsData.value.date_range.from} - ${npsData.value.date_range.to}`,
      20,
      130
    )

    // Key stats on cover
    doc.setFontSize(14)
    doc.text('Summary Statistics', 20, 170)

    // Stats boxes
    const stats = [
      {
        label: 'Total Responses',
        value: npsData.value.overall.total_responses
      },
      {
        label: 'Overall NPS',
        value: npsData.value.overall.nps_score
      },
      {
        label: 'Target Industry NPS',
        value: npsData.value.target_industry_true.nps_score
      }
    ]

    stats.forEach(({ label, value }, index) => {
      const yPos = 180 + index * 40
      doc.setFillColor(...colors.darkGray)
      doc.roundedRect(20, yPos, 170, 30, 3, 3, 'F')
      doc.setFontSize(12)
      doc.text(label, 30, yPos + 12)
      doc.setFontSize(16)
      doc.text(value.toString(), 30, yPos + 25)
    })

    // Distribution page
    doc.addPage()

    doc.setFillColor(...colors.darkGray)
    doc.rect(0, 0, 220, 25, 'F')
    doc.setTextColor(...colors.white)
    doc.setFontSize(16)
    doc.text('Response Distribution', 20, 17)

    // Distribution table
    const distributionData = [
      ['Category', 'Count', 'Percentage'],
      [
        'Promoters',
        npsData.value.overall.promoters,
        (
          (npsData.value.overall.promoters /
            npsData.value.overall.total_responses) *
          100
        ).toFixed(1) + '%'
      ],
      [
        'Passives',
        npsData.value.overall.passives,
        (
          (npsData.value.overall.passives /
            npsData.value.overall.total_responses) *
          100
        ).toFixed(1) + '%'
      ],
      [
        'Detractors',
        npsData.value.overall.detractors,
        (
          (npsData.value.overall.detractors /
            npsData.value.overall.total_responses) *
          100
        ).toFixed(1) + '%'
      ]
    ]

    doc.autoTable({
      startY: 35,
      head: [distributionData[0]],
      body: distributionData.slice(1),
      styles: {
        fontSize: 10,
        cellPadding: 8
      },
      headStyles: {
        fillColor: [...colors.primary],
        textColor: 255,
        fontSize: 11,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [...colors.lightGray]
      },
      margin: { left: 20, right: 20 }
    })

    // Language Analysis page
    if (npsData.value.by_language) {
      doc.addPage()

      doc.setFillColor(...colors.darkGray)
      doc.rect(0, 0, 220, 25, 'F')
      doc.setTextColor(...colors.white)
      doc.setFontSize(16)
      doc.text('NPS by Language', 20, 17)

      const languageData = Object.entries(npsData.value.by_language).map(
        ([code, data]) => {
          try {
            const languageNames = new Intl.DisplayNames(['en'], {
              type: 'language'
            })
            return [
              languageNames.of(code),
              data.nps_score,
              data.total_responses
            ]
          } catch (error) {
            return [code, data.nps_score, data.total_responses]
          }
        }
      )

      doc.autoTable({
        startY: 35,
        head: [['Language', 'NPS Score', 'Total Responses']],
        body: languageData,
        styles: {
          fontSize: 10,
          cellPadding: 8
        },
        headStyles: {
          fillColor: [...colors.primary],
          textColor: 255,
          fontSize: 11,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [...colors.lightGray]
        },
        margin: { left: 20, right: 20 }
      })
    }

    // Industry Analysis page
    if (npsData.value.by_industry) {
      doc.addPage()

      doc.setFillColor(...colors.darkGray)
      doc.rect(0, 0, 220, 25, 'F')
      doc.setTextColor(...colors.white)
      doc.setFontSize(16)
      doc.text('NPS by Industry', 20, 17)

      const industryData = Object.entries(npsData.value.by_industry).map(
        ([industry, data]) => {
          return [industry, data.nps_score, data.total_responses]
        }
      )

      doc.autoTable({
        startY: 35,
        head: [['Industry', 'NPS Score', 'Total Responses']],
        body: industryData,
        styles: {
          fontSize: 10,
          cellPadding: 8
        },
        headStyles: {
          fillColor: [...colors.primary],
          textColor: 255,
          fontSize: 11,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [...colors.lightGray]
        },
        margin: { left: 20, right: 20 }
      })
    }

    // Generate and download
    const pdfOutput = doc.output('blob')
    const url = window.URL.createObjectURL(pdfOutput)
    const link = document.createElement('a')
    link.href = url
    link.download = 'nps-analysis-report.pdf'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error generating PDF:', error)
  }
}

onMounted(() => {
  if (route.query.date_from) {
    dateRange.value.start = new Date(route.query.date_from)
  }
  if (route.query.date_to) {
    dateRange.value.end = new Date(route.query.date_to)
  }

  window.addEventListener('resize', handleResize)
  getNPSData(displayStartDate, displayEndDate)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div v-if="loading"><div class="spinner"></div></div>

  <div
    v-else-if="!npsData"
    class="min-h-screen flex items-center justify-center"
  >
    <div class="text-white">No data available</div>
  </div>
  <div v-else class="min-h-screen">
    <!-- Responsive Header Section -->
    <div
      class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 relative"
    >
      <h2 class="text-xl font-semibold text-white mb-4 md:mb-0">
        NPS Dashboard
        <span
          class="text-sm font-normal text-gray-400 block md:inline-block mt-1 md:mt-0 md:ml-2"
        >
          {{ displayStartDate }} - {{ displayEndDate }}
        </span>
      </h2>

      <!-- Mobile Controls (under md screens) -->
      <div class="flex w-full md:hidden justify-between gap-2">
        <!-- Mobile filter dropdown -->
        <div class="relative w-1/2">
          <select
            v-model="filterType"
            @change="handleFilterChange"
            class="w-full appearance-none bg-gray-800 text-white px-3 py-2 pr-8 rounded-lg border border-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer text-sm"
          >
            <option
              v-for="option in filterOptions"
              :key="option.value"
              :value="option.value"
              class="bg-gray-800"
            >
              {{ option.label }}
            </option>
          </select>
          <div
            class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none"
          >
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        <!-- Mobile menu button -->
        <button
          @click="showMobileMenu = !showMobileMenu"
          class="w-1/2 flex justify-center items-center gap-2 bg-gray-800 px-3 py-2 rounded-lg text-white text-sm"
        >
          <span v-if="!showMobileMenu">Dates and Export</span>
          <span v-else>Hide Options</span>
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              :d="
                showMobileMenu
                  ? 'M6 18L18 6M6 6l12 12'
                  : 'M4 6h16M4 12h16M4 18h16'
              "
            />
          </svg>
        </button>
      </div>

      <!-- Mobile Action Menu -->
      <div
        v-if="showMobileMenu"
        class="w-full md:hidden mt-4 bg-gray-900 rounded-lg p-4 border border-gray-800"
      >
        <div class="space-y-4">
          <!-- Date Range in Mobile Menu -->
          <div>
            <label class="block text-sm text-gray-400 mb-2">Date Range</label>
            <div class="flex flex-col space-y-2">
              <div class="relative">
                <input
                  type="date"
                  v-model="startDate"
                  class="w-full bg-gray-800 text-white border border-gray-700 rounded px-2 py-2 pl-8 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none text-sm"
                />
                <div
                  class="absolute left-0 top-0 h-full flex items-center pl-2 pointer-events-none"
                >
                  <svg
                    class="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div class="relative">
                <input
                  type="date"
                  v-model="endDate"
                  class="w-full bg-gray-800 text-white border border-gray-700 rounded px-2 py-2 pl-8 focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none text-sm"
                />
                <div
                  class="absolute left-0 top-0 h-full flex items-center pl-2 pointer-events-none"
                >
                  <svg
                    class="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Predefined Ranges in Mobile Menu -->
          <div>
            <label class="block text-sm text-gray-400 mb-2">Quick Ranges</label>
            <div class="grid grid-cols-2 gap-2">
              <button
                @click="setPredefinedRange('today')"
                class="px-2 py-2 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Today
              </button>
              <button
                @click="setPredefinedRange('yesterday')"
                class="px-2 py-2 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Yesterday
              </button>
              <button
                @click="setPredefinedRange('week')"
                class="px-2 py-2 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Last 7 Days
              </button>
              <button
                @click="setPredefinedRange('month')"
                class="px-2 py-2 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Last 30 Days
              </button>
            </div>
          </div>

          <!-- Action Buttons in Mobile Menu -->
          <div class="flex gap-2">
            <button
              @click="handleDateRangeChange()"
              showMobileMenu="false"
              class="w-2/3 flex justify-center items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm"
            >
              Apply Dates
            </button>
            <button
              @click="generatePDF()"
              showMobileMenu="false"
              class="w-1/3 flex justify-center items-center gap-1 px-2 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Desktop Controls (md screens and above) -->
      <div class="hidden md:flex items-center gap-4">
        <!-- Type Filter -->
        <div class="relative">
          <select
            v-model="filterType"
            @change="handleFilterChange"
            class="appearance-none bg-gray-800 text-white px-4 py-2 pr-10 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer hover:bg-gray-750"
          >
            <option
              v-for="option in filterOptions"
              :key="option.value"
              :value="option.value"
              class="bg-gray-800 hover:bg-gray-700"
            >
              {{ option.label }}
            </option>
          </select>
          <div
            class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none"
          >
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        <DateRangePicker v-model="dateRange" @change="handleDateRangeChange" />

        <!-- Export Button -->
        <button
          @click="generatePDF()"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors duration-200"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          <span>Export Report</span>
        </button>

        <!-- DateRangePicker Component -->
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-gray-900 rounded-lg p-4">
        <p class="text-sm text-gray-400">Total Responses</p>
        <p class="text-2xl font-bold text-white">
          {{ npsData.overall.total_responses }}
        </p>
      </div>
      <div class="bg-gray-900 rounded-lg p-4">
        <p class="text-sm text-gray-400">Overall NPS</p>
        <p class="text-2xl font-bold text-white">
          {{ npsData.overall.nps_score }}
        </p>
      </div>
      <div class="bg-gray-900 rounded-lg p-4">
        <p class="text-sm text-gray-400">Target Industry NPS</p>
        <p class="text-2xl font-bold text-white">
          {{ npsData.target_industry_true.nps_score }}
        </p>
      </div>
    </div>

    <!-- Distribution Chart -->
    <div v-if="pieChartData" class="bg-gray-900 rounded-lg p-4 mb-6">
      <h3 class="text-lg font-semibold text-white mb-4">
        Response Distribution
      </h3>
      <div class="h-96">
        <Pie
          :data="pieChartData"
          :options="{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: {
                  color: '#fff',
                  font: { size: 12 }
                }
              },
              tooltip: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12
              },
              datalabels: {
                color: '#fff',
                formatter: (value, ctx) => {
                  const label = ctx.chart.data.labels[ctx.dataIndex]
                  const percentage = (
                    (value / npsData.overall.total_responses) *
                    100
                  ).toFixed(1)
                  return `${label}\n${value} (${percentage}%)`
                },
                font: {
                  weight: 'bold',
                  size: 12
                }
              }
            },
            scales: {
              x: { display: false },
              y: { display: false }
            }
          }"
        />
      </div>
    </div>

    <!-- Daily Trends Chart -->
    <div v-if="dailyChartData" class="bg-gray-900 rounded-lg p-4 mb-6">
      <h3 class="text-lg font-semibold text-white mb-4">Daily NPS Trends</h3>
      <div class="h-[400px]">
        <Line :data="dailyChartData" :options="lineChartOptions" />
      </div>
    </div>

    <!-- Cumulative Trends Chart -->
    <div v-if="cumulativeChartData" class="bg-gray-900 rounded-lg p-4 mb-6">
      <h3 class="text-lg font-semibold text-white mb-4">
        Cumulative Daily NPS Trend
      </h3>
      <div class="h-[400px]">
        <Line :data="cumulativeChartData" :options="cumulativeChartOptions" />
      </div>
    </div>

    <!-- Monthly Trends Chart -->
    <div v-if="monthlyChartData" class="bg-gray-900 rounded-lg p-4 mb-6">
      <h3 class="text-lg font-semibold text-white mb-4">Monthly NPS Trends</h3>
      <div class="h-[400px]">
        <Line :data="monthlyChartData" :options="lineChartOptions" />
      </div>
    </div>

    <!-- Cumulative Monthly Trends Chart -->
    <div
      v-if="cumulativeMonthlyChartData"
      class="bg-gray-900 rounded-lg p-4 mb-6"
    >
      <h3 class="text-lg font-semibold text-white mb-4">
        Cumulative Monthly NPS Trend
      </h3>
      <div class="h-[400px]">
        <Line
          :data="cumulativeMonthlyChartData"
          :options="cumulativeChartOptions"
        />
      </div>
    </div>

    <!-- Language Chart -->
    <div v-if="languageChartData" class="bg-gray-900 rounded-lg p-4 mb-6">
      <h3 class="text-lg font-semibold text-white mb-4">NPS by Language</h3>
      <div class="h-[400px]">
        <BarChart :chart-data="languageChartData" :options="chartOptions" />
      </div>
    </div>

    <!-- Industry Chart -->
    <div v-if="industryChartData" class="bg-gray-900 rounded-lg p-4">
      <h3 class="text-lg font-semibold text-white mb-4">NPS by Industry</h3>
      <div class="h-[600px]">
        <BarChart
          :chart-data="industryChartData"
          :options="{
            ...chartOptions,
            indexAxis: 'y',
            scales: {
              ...chartOptions.scales,
              y: {
                ...chartOptions.scales.y,
                ticks: {
                  ...chartOptions.scales.y.ticks,
                  font: {
                    size: 11
                  }
                }
              }
            }
          }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hide date picker default icon */
input[type='date']::-webkit-calendar-picker-indicator {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
}
</style>